import dayjs, { Dayjs } from 'dayjs'

/**
 * Convert hh:mm string into minutes
 * @param aTime 
 * @returns number of minutes
 */
export const timeHhMmToMins = (aTime: string | null): number | null => {
  if (!aTime) return null

  let hours: number, mins: number

  // Using substring instead of the deprecated substr method
  if (isNaN(parseInt(aTime.substring(0, 2))) || isNaN(parseInt(aTime.substring(3, 5)))) {
    return null
  }

  if (aTime.length === 5) {
    // we are expecting aTime in format 'hh:mm'
    hours = parseInt(aTime.substring(0, 2)) || 0 // Parsing hours
    mins = parseInt(aTime.substring(3, 5)) || 0  // Parsing minutes

    return (hours * 60) + mins
  }

  return null
}

/**
 * Convert minutes into hh:mm. If amPmFlag is set to true, return hh:mm aa
 * @param aTime 
 * @returns number of minutes
 */
export const timeMinsToHhMm = (aTime: number, amPmFlag: boolean = false): string | null => {
  if (aTime === null) {
    return null
  }

  if (isNaN(parseInt(aTime.toString()))) {
    return null
  }

  let hoursInt = Math.floor(aTime / 60)
  let hours = ('00' + hoursInt).substring(('00' + hoursInt).length - 2)
  const minutesInt = aTime % 60
  const mins = ('00' + minutesInt).substring(('00' + minutesInt).length - 2)

  if (amPmFlag) {
    if (hoursInt < 12) {
      return `${hours}:${mins} AM`
    } else {
      hoursInt = hoursInt > 12 ? hoursInt - 12 : 12 // Adjust for PM hours
      hours = ('00' + hoursInt).substring(('00' + hoursInt).length - 2)

      return `${hours}:${mins} PM`
    }
  }

  return `${hours}:${mins}`
}

/**
 * Convert hh:mm:ss into DayJs duration object
 * @param aTime 
 * @returns number of minutes
 */
export const timeHhMmSsToDayjs = (aTime?: string): Dayjs | null => {
  if (aTime === null || !aTime) {
    return null
  }

  const parts = aTime.split(':')

  // Create a duration object from the parts
  const duration = dayjs.duration({
    hours: parseInt(parts[0], 10),
    minutes: parseInt(parts[1], 10),
    seconds: parts.length === 3 ? parseInt(parts[2], 10) : 0,
  })

  const todayAtMidnight = dayjs().startOf('day')

  return todayAtMidnight.add(duration)
}

/**
 * Convert DayJs duration object into hh:mm:ss
 * @param aDate Dayjs
 * @returns string (hh:mm:ss)
 */
export const timeDayjsToHhMmSs = (aDate?: Dayjs | null): string | null => {
  if (aDate === null || !aDate) {
    return null
  }

  const hours = aDate.hour().toString().padStart(2, '0')
  const minutes = aDate.minute().toString().padStart(2, '0')
  const seconds = aDate.second().toString().padStart(2, '0')

  return `${hours}:${minutes}:${seconds}`
}

/**
 * Convert DayJs duration object into hh:mm
 * @param aDate Dayjs
 * @returns string (hh:mm:ss)
 */
export const timeDayjsToHhMm = (aDate?: Dayjs | null): string | null => {
  if (aDate === null || !aDate) {
    return null
  }

  const hours = aDate.hour().toString().padStart(2, '0')
  const minutes = aDate.minute().toString().padStart(2, '0')

  return `${hours}:${minutes}`
}

/**
 * Convert duration in minutes to hours in hh:mm format
 * @param minutes number of minutes
 * @returns string in hh:mm format
 */
export const timeMinsToHours = (minutes: number): string | null => {
  if (minutes === null || isNaN(minutes)) {
    return null
  }

  const hours = Math.floor(minutes / 60)
  const mins = Math.round((minutes / 60 - hours) * 60)

  const paddedHours = hours.toString().padStart(2, '0')
  const paddedMins = mins.toString().padStart(2, '0')

  return `${paddedHours}:${paddedMins}`
}

/**
 * Combine hh:mm format string with today's date 
 * @param timeStr 
 * @returns DayJs object
 */
export const timeWithTodayToDayJs = (timeStr: string) => {
  const time = dayjs(timeStr, 'HH:mm')
  const today = dayjs().startOf('day').set('hour', time.hour()).set('minute', time.minute())
  
  return today
}
